import * as React from 'react';

import {
	GlobalizationProvider,
	GlobalizationProviderProps,
	LoadingProvider,
	SiteConfig,
	SiteConfigProvider,
} from '../../contexts';
import Pages, { Page } from '../../hocs/Pages';
import {
	selectIsAppLoading,
	useSelectNamespace,
	useSelectUserContext,
} from '../../selectors';
import { HeaderMenuOption, NotificationConfig } from '../../types';
import { ApplicationContext } from '../../types/application.types';
import { App, StyledAppBody } from './ApplicationWrapper.styles';

import { AboutBoxProps } from '../../components/AboutBox';
import { MoveableChatbot } from '../../components/Chatbot/';
import ErrorBoundary from '../../components/ErrorBoundary';
import ExpiredTenantDialogWrapper from '../../components/ExpiredTenantDialog';
import LoadingPage from '../../components/LoadingPage';
import { LocalizationProviderLuxon } from '../../components/MuiExposes/MuiLocalizationProviders';
import { AnalyticsProviderWrapper } from '../../contexts/AnalyticsProvider/AnalyticsProviderWrapper';
import { AppInitializer } from '../../hooks/useInitialization';
import AuthenticationRouter from '../../contexts/AuthenticationWrapper/AuthenticationRouter';
import { LogoutContextProvider } from '../../contexts/AuthenticationWrapper/LogoutContext';
import { FeatureFlagProvider } from '../../contexts/FeatureFlag';
import NotificationHubProvider from '../../contexts/NotificationHubProvider';
import { NotificationHub } from '../../contexts/NotificationHubProvider/NotificationHubProvider.types';
import { AppBranding } from '../../types/appBranding';
import Header from './Components/Header/Header';
import LocalStorageStatesSynchronizer from './Components/LocalStorageStatesSynchronizer';
import ModalsForUserHelp from './Components/ModalsForUserHelp/ModalsForUserHelp';
import NavigateToProvider from './Components/NavigateToProvider';
import AboutToExpireModalHost from './features/AboutToExpire/AboutToExpireModalHost';
// ApplicationWrapper sub-components
import { Theme } from '@mui/material';
import createGenerateClassName from '@mui/styles/createGenerateClassName';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { setApplicationContext } from '../../actions';
import Notifier from '../../components/Notifier';
import { TermsAndConditionsWrapper } from '../../components/TermsAndConditionsDialog';
import VerifyUserTeamAccessWrapper from '../../components/UserTeamAccess/VerifyUserTeamAccessWrapper';
import { S3DataAuthProvider } from '../../contexts/S3DataAuth';
import SettingsProvider from '../../contexts/Settings';
import { useProjects } from '../../hooks/useProjects';
import useInferTenantId from '../../storages/useInferTenantId';
import { UserContext } from '../../types/userContext';
import NotificationManagement from './Components/Header/StylovyzeNotification/NotificationManagement';
import { WhatsNewDetailIFrame } from './Components/Header/WhatsNewDetaiIIFrame';
import { WhatsNewPage } from './Components/Header/WhatsNewPage';
import { ThemeStylesProvider } from './theme';
import { INFO360_THEMES } from './theme/theme.types';

declare module '@mui/styles/defaultTheme' {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	interface DefaultTheme extends Theme {}
}

function isAuthorized(userContext?: UserContext) {
	return userContext?.defaultSite && userContext?.innovyzeRole;
}
export interface ApplicationWrapperProps {
	/**
	 * The Auth0 config setup. This must either be passed in directly or fetched with the configUrl prop.
	 * Check sample code above for SiteConfig Type definition.
	 */
	config?: SiteConfig | null;
	/**
	 * The url where the application should look for our site-config.js file. Current standard is to keep it
	 * in your application's public folder. Default value maintains this standard.
	 */
	configUrl?: string;
	/**
	 * An array of the pages in your application. Check sample code above for Page Type definition.
	 */
	pages: Page[];
	/**
	 * Whether to display the side Nav or not.
	 */
	showSideNav?: boolean;
	/**
	 * Your application's theme
	 */
	theme?: Theme;
	/**
	 * Should the Auth0 authenticator be run? Defaults to true. Really
	 * should only be false in testing/story situations
	 */
	authenticate?: boolean;
	/**
	 * The application branding. Defaults to 'info360'. New logos
	 * can be added in Components/Header/Branding/Branding.tsx.
	 */
	branding?: AppBranding;
	/**
	 * Whether to display the org switcher or not.
	 */
	isTenantAware?: boolean;
	/**
	 * The help url for your component.
	 */
	helpUrl?: string | false;

	/**
	 * Changes help button into a dropdown menu if supplied.
	 */
	aboutBox?: Omit<AboutBoxProps, 'open' | 'setOpen'>;
	/*
	 * Force users to accept terms and conditions before using the app.
	 */
	validateTerms?: boolean;
	/*
	 * If containing a subscription set, the application will render a modal if subscriptions in that set are expired or inexistent for specific tenant.
	 */
	expiredSubscriptionsSet?: Set<string>;
	/*
	 * Set Application Context
	 */
	applicationContext?: ApplicationContext;
	/**
	 * globalization provider
	 */
	globalization?: Omit<GlobalizationProviderProps, 'children'>;
	/**
	 * injectTenantId: Whether to inject tenantId into the request headers.
	 */
	injectTenantId?: boolean;
	/**
	 * helpModals: a boolean to enable or disable the help modals
	 * @default false
	 */
	helpModals?: boolean;
	notificationHub?: NotificationHub;
	withHelpbot?: boolean;
	readonly hasSharedWorkers?: boolean;
	customHelpOptions?: HeaderMenuOption[];
	customProfileOptions?: HeaderMenuOption[];
	/**
	 * Whether or not the application should display as the landing page. Reduces various functionality.
	 */
	isLandingPage?: boolean;
	defaultTheme?: INFO360_THEMES;
}

/**
 * A wrapper with the entire frontend setup for our modules. In addition to applying global styles and including the Header,
 * Nav, and Page portions of your application, it also wraps it with the site-config, Auth-0, and
 * Notistack providers.
 */
export const ApplicationWrapper = ({
	globalization,
	configUrl,
	config,
	pages,
	authenticate,
	theme,
	showSideNav = true,
	branding,
	isTenantAware = true,
	isLandingPage = false,
	helpUrl,
	aboutBox,
	validateTerms,
	applicationContext,
	expiredSubscriptionsSet,
	injectTenantId = false,
	helpModals = false,
	withHelpbot = false,
	notificationHub,
	hasSharedWorkers = false,
	customHelpOptions,
	customProfileOptions,
	defaultTheme,
}: ApplicationWrapperProps): JSX.Element => {
	const appName = useSelectNamespace();
	let authorizedPages: Page[] = [];
	const dispatch = useDispatch();
	const userContext = useSelectUserContext();

	const isLoading = selectIsAppLoading();
	const { initializeProjects, initializeProjectBySourceId } = useProjects();

	const { inEmulation } = userContext;
	if (isLoading) {
		authorizedPages = [
			{
				to: '*',
				render: <LoadingPage />,
			},
		];
	} else if (isAuthorized(userContext)) {
		authorizedPages = [
			...pages,
			{
				to: '/whats-new',
				render: <WhatsNewPage />,
			},
			{
				to: '/whats-new/detail',
				render: <WhatsNewDetailIFrame />,
			},
			{
				to: '/notification-management',
				render: <NotificationManagement />,
			},
		];
	} else {
		// Should solve for occasional unauthorized page popping briefly up unexpectedly due to race conditions
		// TODO: Remove this once we eliminate Auth0 login flow.
		authorizedPages = [
			{
				to: '*',
				render: <LoadingPage />,
			},
		];
	}

	const generateClassName = createGenerateClassName({
		productionPrefix: 'inno',
	});
	const notificationConfig: NotificationConfig | undefined =
		!!notificationHub?.applicationId &&
		!!notificationHub?.tenantId &&
		!!notificationHub?.userId &&
		!!notificationHub?.product
			? {
					applicationId: notificationHub.applicationId,
					tenantId: notificationHub.tenantId,
					userId: notificationHub.userId,
					product: notificationHub.product,
			  }
			: undefined;

	// Effects
	useInferTenantId(injectTenantId);
	React.useEffect(() => {
		initializeProjects();
		initializeProjectBySourceId();

		if (applicationContext && !appName) {
			dispatch(setApplicationContext(applicationContext));
		}
	}, [applicationContext]);

	// TODO - there are way too many styles/theme providers to be needed here
	const partialApp = React.useMemo(
		() => (
			<Router>
				<NavigateToProvider />
				<LocalStorageStatesSynchronizer />

				<SiteConfigProvider url={configUrl} config={config}>
					<AppInitializer isLandingPage={isLandingPage} />
					<ModalsForUserHelp helpModals={helpModals} />
					<LogoutContextProvider>
						<AuthenticationRouter
							authenticate={authenticate}
							hasSharedWorkers={hasSharedWorkers}
							isLandingPage={isLandingPage}>
							<FeatureFlagProvider>
								<AboutToExpireModalHost />
								<SettingsProvider>
									<AnalyticsProviderWrapper>
										<LoadingProvider branding={branding}>
											<S3DataAuthProvider
												isLandingPage={isLandingPage}>
												<Notifier maxSnack={3}>
													<LocalizationProviderLuxon>
														<NotificationHubProvider
															notificationHub={
																notificationHub
															}>
															<ErrorBoundary>
																<App id="inno-app">
																	{withHelpbot ? (
																		<MoveableChatbot />
																	) : null}
																	<Header
																		branding={
																			branding as AppBranding
																		}
																		isTenantAware={
																			isTenantAware
																		}
																		isLandingPage={
																			isLandingPage
																		}
																		helpUrl={
																			helpUrl
																		}
																		aboutBox={
																			aboutBox
																		}
																		inEmulation={
																			inEmulation
																		}
																		notificationConfig={
																			notificationConfig
																		}
																		withHelpbot={
																			withHelpbot
																		}
																		customHelpOptions={
																			customHelpOptions
																		}
																		customProfileOptions={
																			customProfileOptions
																		}
																	/>
																	<StyledAppBody id="inno-app-body">
																		<VerifyUserTeamAccessWrapper
																			isLandingPage={
																				isLandingPage
																			}>
																			<TermsAndConditionsWrapper
																				validateTerms={
																					validateTerms
																				}>
																				<ExpiredTenantDialogWrapper
																					expiredSubscriptionsSet={
																						expiredSubscriptionsSet
																					}
																					isTenantAware={
																						isTenantAware
																					}>
																					<Pages
																						pages={
																							authorizedPages
																						}
																						showSideNav={
																							showSideNav
																						}
																					/>
																				</ExpiredTenantDialogWrapper>
																			</TermsAndConditionsWrapper>
																		</VerifyUserTeamAccessWrapper>
																	</StyledAppBody>
																</App>
															</ErrorBoundary>
														</NotificationHubProvider>
													</LocalizationProviderLuxon>
												</Notifier>
											</S3DataAuthProvider>
										</LoadingProvider>
									</AnalyticsProviderWrapper>
								</SettingsProvider>
							</FeatureFlagProvider>
						</AuthenticationRouter>
					</LogoutContextProvider>
				</SiteConfigProvider>
			</Router>
		),
		[
			generateClassName,
			theme,
			configUrl,
			config,
			authenticate,
			branding,
			isTenantAware,
			helpUrl,
			aboutBox,
			authorizedPages,
			showSideNav,
		],
	);
	const styledApp = (
		<ThemeStylesProvider theme={theme} defaultTheme={defaultTheme}>
			{partialApp}
		</ThemeStylesProvider>
	);
	return globalization ? (
		<GlobalizationProvider {...globalization}>
			{styledApp}
		</GlobalizationProvider>
	) : (
		styledApp
	);
};

ApplicationWrapper.defaultProps = {
	config: null,
	authenticate: true,
};

export default React.memo(ApplicationWrapper);
